import { CheckCircle2, AlertTriangle, XCircle, ShieldAlert } from "lucide-react";
import FoundationParamsView from "./FoundationParamsView";

const OUTCOME_STYLE = {
  success:          { cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: CheckCircle2 },
  validation_error: { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: AlertTriangle },
  sql_error:        { cls: "bg-rose-50 border-rose-200 text-rose-800", Icon: XCircle },
  bridge_error:     { cls: "bg-rose-50 border-rose-200 text-rose-800", Icon: XCircle },
  blocked:          { cls: "bg-rose-50 border-rose-200 text-rose-800", Icon: ShieldAlert },
  rejected:         { cls: "bg-amber-50 border-amber-200 text-amber-800", Icon: AlertTriangle },
};

function Raw({ label, value, testId }) {
  const shown = value === null || value === undefined ? "—" : String(value);
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-slate-500 w-28 shrink-0">{label}</span>
      <code className="text-slate-800 break-all" data-testid={testId}>{shown}</code>
    </div>
  );
}

// Friendly message on top; every raw value always visible underneath.
export default function FoundationSpResultCard({ result, testIdPrefix = "sp" }) {
  if (!result) return null;
  const style = OUTCOME_STYLE[result.outcome] || OUTCOME_STYLE.sql_error;
  const { Icon } = style;
  const sets = result.result_sets || [];
  const rb = result.readback;
  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-result`}>
      <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${style.cls}`}
           data-testid={`${testIdPrefix}-result-message`}>
        <Icon className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="text-sm font-medium">{result.message}</div>
      </div>

      {(result.params_sent || result.withheld?.length || result.errors?.length) && (
        <FoundationParamsView params={result.params_sent || {}} withheld={result.withheld || []}
                              blockers={result.errors || []} truncated={Array.isArray(result.truncated) ? result.truncated : []}
                              omittedUnchanged={result.omitted_params || []}
                              database={result.target_database || result.required_database} testIdPrefix={`${testIdPrefix}-sent`} />
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1">
        <Raw label="Return Code" value={result.return_code} testId={`${testIdPrefix}-raw-rc`} />
        <Raw label="@FSI_error" value={result.fsi_error} testId={`${testIdPrefix}-raw-fsi`} />
        {result.fsi_error_text && <Raw label="FSI Meaning" value={result.fsi_error_text} />}
        <Raw label="Target DB" value={result.target_database} testId={`${testIdPrefix}-raw-db`} />
        <Raw label="Login" value={result.login} />
        <Raw label="Job no (sent)" value={result.job_no} testId={`${testIdPrefix}-raw-jobno`} />
        <Raw label="Description" value={result.description_sent ?? result.description} />
        <Raw label="Truncated" value={Array.isArray(result.truncated) ? (result.truncated.length ? result.truncated.map(t => t.param).join(", ") : "no") : (result.truncated ? "yes" : "no")} testId={`${testIdPrefix}-raw-truncated`} />
        <Raw label="Exception" value={result.exception_text} testId={`${testIdPrefix}-raw-exception`} />
        {result.log_id && <Raw label="Log Id" value={result.log_id} />}
      </div>

      <div className="text-xs">
        <div className="font-semibold text-slate-600 mb-1">Result sets from the SP</div>
        {sets.length === 0 ? (
          <div className="text-slate-400" data-testid={`${testIdPrefix}-result-sets-empty`}>None emitted.</div>
        ) : sets.map((rows, i) => (
          <pre key={i} className="bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto text-[11px]"
               data-testid={`${testIdPrefix}-result-set-${i}`}>{JSON.stringify(rows, null, 1)}</pre>
        ))}
      </div>

      {rb && (
        <div className="text-xs" data-testid={`${testIdPrefix}-readback`}>
          <div className="font-semibold text-slate-600 mb-1">Readback — dbo.jobs</div>
          {rb.ok && rb.rows?.length > 0 ? (
            <table className="w-full border border-slate-200 rounded-md overflow-hidden">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr><th className="px-2 py-1">job_no</th><th className="px-2 py-1">description</th><th className="px-2 py-1">customer</th><th className="px-2 py-1">PM</th><th className="px-2 py-1">class</th><th className="px-2 py-1">status</th><th className="px-2 py-1">contract</th><th className="px-2 py-1">start</th></tr>
              </thead>
              <tbody>
                {rb.rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`${testIdPrefix}-readback-row-${i}`}>
                    <td className="px-2 py-1 font-mono">{String(r.job_no ?? "").trim()}</td>
                    <td className="px-2 py-1">{r.description}</td>
                    <td className="px-2 py-1 font-mono">{r.customer_no ?? ""}</td>
                    <td className="px-2 py-1 font-mono">{r.project_manager_no ?? ""}</td>
                    <td className="px-2 py-1 font-mono">{r.project_class_no ?? ""}</td>
                    <td className="px-2 py-1 font-mono">{r.job_status ?? ""}</td>
                    <td className="px-2 py-1 font-mono">{r.original_contract ?? ""}</td>
                    <td className="px-2 py-1 font-mono">{String(r.job_start_date ?? "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : rb.ok ? (
            <div className="text-amber-700">Readback returned no rows — verify in Foundation UI.</div>
          ) : (
            <div className="text-amber-700">{rb.message}</div>
          )}
        </div>
      )}
    </div>
  );
}
